import React, { forwardRef, useMemo } from "react";
import "devextreme/dist/css/dx.light.css";
import DataGrid, {
  Column,
  FilterRow,
  Paging,
  RemoteOperations,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { useMsal } from "@azure/msal-react";
import PropTypes from "prop-types";
import { Button } from "devextreme-react/button";
import { resViewIconSVG } from "../../Assets/SVGs/ResourceSVGs";
import { appRequest } from "../../authentication/authConfig";
import { resourceGridIcon, resourceGridStatus } from "../DatagridCells/DatagridCells";
import { getResourceTypeDisplayName } from "../../utilities";
import "./ResourceDataGrid.scss";

function getLatestVersion(versions) {
  if (!versions || versions.length === 0) {
    return "N/A";
  }
  versions.sort((a, b) =>
    b.versionNumber.localeCompare(a.versionNumber, undefined, {
      numeric: true,
      sensitivity: "base",
    })
  );
  return versions[0].versionNumber;
}

const ResourceDataGrid = forwardRef((props, ref) => {
  const { handleViewResourceCallback, isViewResourcePageVisible } = props;

  const { instance, accounts } = useMsal();
  const dataSource = useMemo(() => {
    const store = createStore({
      key: "id",
      loadUrl: "/api/Resource/get-all",
      onBeforeSend: async (method, ajaxOptions) => {
        try {
          const request = {
            ...appRequest,
            account: accounts[0],
          };
          const response = await instance.acquireTokenSilent(request);
          const { accessToken } = response;
          const xsrfToken = localStorage.getItem("csrf");

          // eslint-disable-next-line no-param-reassign
          ajaxOptions.headers = {
            "X-XSRF-TOKEN": xsrfToken,
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          };
        } catch (error) {
          console.error("Authorization Error: ", error);
          throw error;
        }
      },
    });

    return store;
    // eslint-disable-next-line
  }, [instance, accounts, isViewResourcePageVisible]);

  const renderViewResourceButton = (cellData) => (
    <Button
      className="viewDivResource"
      onClick={() => {
        handleViewResourceCallback(cellData);
      }}
      stylingMode="text"
    >
      {resViewIconSVG}
    </Button>
  );

  return (
    <div className="datagrid-resource">
      <DataGrid
        height="100%"
        columnAutoWidth={true}
        allowColumnResizing={true}
        wordWrapEnabled={true}
        dataSource={dataSource}
        hoverStateEnabled={true}
        showBorders={true}
        noDataText="No resources available"
        ref={ref}
      >
        <RemoteOperations sorting paging filtering grouping />
        <FilterRow visible={true} />
        <Paging defaultPageSize={20} />
        <Column
          width={50}
          caption=""
          allowFiltering={false}
          dataField="isClinithinkResource"
          cellRender={resourceGridIcon}
          allowSorting={false}
        />
        <Column dataField="name" caption="Resource name" sortIndex={1} sortOrder="asc" />
        <Column
          dataField="resourceTypeId"
          caption="Resource type"
          customizeText={({ value }) => getResourceTypeDisplayName(value.toUpperCase())}
          allowFiltering={false}
        />
        <Column
          cellRender={({ data }) => getLatestVersion(data.resourceVersions)}
          alignment="left"
          caption="Latest version"
          allowHeaderFiltering={false}
          allowFiltering={false}
        />
        <Column
          caption="Versions available"
          cellRender={({ data }) => data.resourceVersions.length}
        />
        <Column
          dataField="subscribedCount"
          alignment="left"
          caption="Total Subscriptions"
          allowHeaderFiltering={false}
          allowFiltering={false}
        />
        <Column
          dataField="active"
          caption="Status"
          dataType="string"
          cellRender={resourceGridStatus}
          allowFiltering={false}
          sortOrder="desc"
          sortIndex={0}
        />
        <Column
          caption="Actions"
          type="buttons"
          name="resViewButton"
          cellRender={(cellData) => renderViewResourceButton(cellData)}
        />
      </DataGrid>
    </div>
  );
});

ResourceDataGrid.propTypes = {
  handleViewResourceCallback: PropTypes.func.isRequired,
  isViewResourcePageVisible: PropTypes.bool.isRequired,
};
export default ResourceDataGrid;
